import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { VdoSessionState } from '@pcp/auth';
import { provideTranslocoScope, TranslocoDirective } from '@jsverse/transloco';
import { Observable, map, of } from 'rxjs';
import { EmmIframeComponent } from 'shared/classroom/preview-screen/slide-rendering/ui';
import { VDO_SESSION } from '../difficult-question-element/injection-tokens';
import { buildVdoSimulatorUrl } from './vdo-simulator-url';

/** Both halves of what the template shows. `toView` is the only place that builds one. */
interface VdoView {
    src: null | string;
    unavailable: boolean;
}

@Component({
    selector: 'pcp-vdo-element',
    templateUrl: './vdo-element.component.html',
    styleUrls: ['./vdo-element.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [EmmIframeComponent, AsyncPipe, TranslocoDirective],
    providers: [provideTranslocoScope({ scope: 'vdoElement' })],
})
export class VdoElementComponent {
    // optional: true so a component built outside a forXxx() factory - a story, a spec - renders
    // nothing instead of raising NullInjectorError.
    private readonly sessionState: null | Observable<null | VdoSessionState> =
        inject(VDO_SESSION, { optional: true });

    /** No take(1): the token is not refreshable, so a replacement session means a new URL. */
    readonly view$: Observable<VdoView> = (this.sessionState ?? of(null)).pipe(map(toView));
}

const toView = (state: null | VdoSessionState): VdoView => {
    // No session bound at all: this app has no simulator, so the preview stays empty.
    if (state === null) {
        return { src: null, unavailable: false };
    }

    // A session whose URL fails validation is as unusable as no session, and is reported the same
    // way - never as an empty preview, which would claim there is no simulator here.
    const src = state.status === 'session' ? buildVdoSimulatorUrl(state.session) : null;
    return { src, unavailable: src === null };
};
